import { Tooltip } from 'bootstrap';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ActivationSummary, ActivationStage } from '../WatchlistDashboardService';
import AlumniRow from './AlumniRow';
import { buildReportLink } from '../reportLink';
import { getStageDescriptions } from '../../../watchlists/report/labelMaps';

// Stage chip styling by pipeline type (matches the mockup).
const stageClass = (type: ActivationStage['type']) => {
    switch (type) {
        case 'value':
            return 'bg-success bg-opacity-10 text-success border border-success border-opacity-25';
        case 'dormant':
            return 'bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25';
        case 'denied':
            return 'bg-secondary bg-opacity-25 text-dark border border-secondary border-opacity-25';
        default:
            return 'bg-secondary bg-opacity-10 text-dark-blue';
    }
};

/**
 * Layer 3 Activation card: one outcome's stage pipeline + value zone + top value-zone alumni.
 */
const ActivationCard = ({ summary, instituteId, filterInstituteId }: { summary: ActivationSummary; instituteId: string | number; filterInstituteId?: string | null }) => {
    const pipelineRef = useRef<HTMLDivElement>(null);

    // Bootstrap tooltips on stage chips — same pattern as AlumniRow.
    useEffect(() => {
        if (!pipelineRef.current) return;
        const tooltips = Array.from(pipelineRef.current.querySelectorAll('[data-bs-toggle="tooltip"]'))
            .map((el) => new Tooltip(el as HTMLElement, { container: 'body' }));
        return () => tooltips.forEach((t) => t.dispose());
    }, [summary.outcome]);

    const descriptions = getStageDescriptions(summary.outcome);

    return (
        <div className="col">
            <div className="bg-white border rounded-4 p-4 h-100 d-flex flex-column justify-content-between shadow-sm border-success border-opacity-25">
            <div>
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <div className="font-16 font-600 text-dark">{summary.label} Activation Watchlist</div>
                        <div className="font-12 text-secondary mb-2">Alumni progressing through structured engagement stages</div>
                    </div>
                    <div className="text-end">
                        <div className="font-24 font-600 text-dark lh-base">{summary.total_in_outcome}</div>
                        <div className="font-12 text-secondary">total alumni</div>
                    </div>
                </div>

                {/* Stage pipeline */}
                <div ref={pipelineRef} className="d-flex align-items-center gap-1 flex-wrap mb-3">
                    {summary.stages.map((stage, index) => (
                        <div key={stage.code} className="d-flex align-items-center gap-1 flex-fill">
                            <div
                                className={`text-center p-2 rounded-3 flex-fill ${stageClass(stage.type)}`}
                                style={{ minWidth: 60, cursor: 'help' }}
                                data-bs-toggle={descriptions ? 'tooltip' : undefined}
                                data-bs-title={descriptions?.[stage.code] ?? stage.label}>
                                <div className="font-18 font-600">{stage.count}</div>
                                <div className="font-10 text-muted text-uppercase">{stage.label}</div>
                            </div>
                            {index <= 4 && (
                                <span className="align-self-center font-12 text-muted">→</span>
                            )}
                        </div>
                    ))}
                </div>

                {/* Value zone */}
                <div className="d-flex align-items-center gap-2 py-2 px-3 border rounded-3 bg-success bg-opacity-10 text-success border-success border-opacity-25 mb-3">
                    <span className="font-14">⭐</span>
                    <span className="font-12 font-600">Value Zone — {summary.value_zone.count} alumni (Qualified + Contributing)</span>
                    <span className="ms-auto font-13 text-muted">{summary.value_zone.pct}% of pipeline</span>
                </div>

                <hr className="border-success" />
                <div className="font-10 font-600 text-uppercase text-muted mb-2">Top Alumni in Value Zone</div>
                <div className="d-flex flex-column gap-1">
                    {summary.top_alumni.length === 0 ? (
                        <div className="font-12 text-muted">No alumni in the value zone yet</div>
                    ) : (
                        summary.top_alumni.map((alumni) => <AlumniRow key={alumni.student_id} alumni={alumni} />)
                    )}
                </div>
            </div>
            {summary.total_in_outcome > 0 && (
                <Link
                    to={buildReportLink({ institute_id: instituteId, filter_institute_id: filterInstituteId, layer: 'activation', outcome: summary.outcome })}
                    className="d-inline-flex align-items-center gap-1 mt-3 font-12 font-600 text-primary text-decoration-none">
                    View Full Pipeline →
                </Link>
            )}
        </div>
    </div>
    );
};

export default ActivationCard;
